import {
  DEFAULT_PAYROLL_CYCLE,
  PAY_TYPE_OPTIONS,
  useGenerateSalaryMutation,
  useGetPayrollAbleEmployeesQuery,
  useGetPayrollCalendarQuery,
  type GenerateSalaryBody,
  type PayType,
} from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import EmployeeSelect from '@/components/EmployeeSelect';
import { ChevronDownIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { formatDate } from '@/utils';
import { Button, Flex, Form, Select } from 'antd';
import dayjs from 'dayjs';
import { flattenAssigned } from '../shared/payroll.helpers';
import CyclePeriodPicker from './CyclePeriodPicker';

type Props = {
  open: boolean;
  handleClose: () => void;
};

/**
 * The period picker hands over a dayjs when the cycle is a calendar month, and
 * already normalized values when it is a custom cycle. The API wants a numeric
 * year and a two digit month either way.
 */
const toYear = (value: unknown) =>
  dayjs.isDayjs(value) ? value.year() : Number(value);

const toMonth = (value: unknown) =>
  dayjs.isDayjs(value)
    ? value.format('MM')
    : String(value ?? '').padStart(2, '0');

/**
 * Generate Payroll: pick the population (monthly or daily), the period its
 * calendar allows, and the employees to pay. One batch is created per run.
 */
const ManagePayroll = ({ open, handleClose }: Props) => {
  const [form] = Form.useForm();
  const assignedEmployees = Form.useWatch('assigned_employees', form);
  const selectedYear = Form.useWatch('year', form);
  const selectedMonth = Form.useWatch('month', form);
  const payType: PayType = Form.useWatch('payFor', form) || 'MONTHLY';
  const isDaily = payType === 'DAILY';

  const [generateSalary, { isLoading: isGenerating }] =
    useGenerateSalaryMutation();

  const { data: calendarData } = useGetPayrollCalendarQuery(payType, {
    skip: !open,
  });
  const cycleConfig = calendarData?.data?.calendar || DEFAULT_PAYROLL_CYCLE;

  // One list for both populations: the employee endpoint filters by pay basis
  // and, once a month is chosen, by who can still be paid for it.
  const { data: employeeData } = useGetPayrollAbleEmployeesQuery({
    params: {
      skip: 0,
      limit: 0,
      status: 'active',
      pay_basis: payType,
      ...(selectedMonth && selectedYear
        ? {
            payroll_month: toMonth(selectedMonth),
            payroll_year: toYear(selectedYear),
          }
        : {}),
    },
  });
  const employees = employeeData?.data?.data || [];

  const closeDrawer = () => {
    form.resetFields();
    handleClose();
  };

  const handleSubmit = async (values: any) => {
    const { year, month, cycleNo, assigned_employees } = values || {};
    const userIds = flattenAssigned(assigned_employees);

    // The picker's own rule accepts an empty selection, so it is checked here.
    if (!userIds.length) {
      form.setFields([
        { name: 'assigned_employees', errors: ['Please select employees'] },
      ]);
      return;
    }

    try {
      // The server derives the period from the calendar. A daily month with
      // several cycles also needs the chosen one, or every cycle is generated.
      const payload: GenerateSalaryBody = {
        payFor: payType,
        year: toYear(year),
        month: toMonth(month),
        userIds,
        ...(isDaily && cycleNo ? { cycleNo: Number(cycleNo) } : {}),
      };

      await generateSalary(payload).unwrap();
      toast('success', 'Salary generation started.');
      closeDrawer();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to generate salary'));
    }
  };

  return (
    <CustomDrawer
      width={800}
      open={open}
      onClose={closeDrawer}
      title="Generate Payroll"
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-6"
        onFinish={handleSubmit}
      >
        <Form.Item initialValue={{}} noStyle name="assigned_employees" />

        <Form.Item
          label="Payroll For"
          name="payFor"
          initialValue="MONTHLY"
          rules={[{ required: true, message: 'Please select pay type' }]}
          extra={
            <span className="text-white/40 text-xs">
              Monthly and daily employees run in separate batches because their
              cycles differ.
            </span>
          }
        >
          <Select
            options={PAY_TYPE_OPTIONS}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={() =>
              form.setFieldsValue({
                year: undefined,
                month: undefined,
                cycle_month: undefined,
                cycleNo: undefined,
                assigned_employees: {},
              })
            }
          />
        </Form.Item>

        <CyclePeriodPicker cycle={cycleConfig} payType={payType} />

        <EmployeeSelect
          form={form}
          employees={employees}
          showJoinDate={!isDaily}
        />

        <EmployeeSelect.Selected
          form={form}
          employees={employees}
          allowRemoveEmployee
          assignedEmployees={assignedEmployees || {}}
          renderExtraInfo={(user: any) =>
            isDaily ? null : (
              <Flex gap={4} className="mt-0.5">
                <span className="text-xs text-white/60">
                  Joined: {formatDate(user?.joining_date)}
                </span>
              </Flex>
            )
          }
        />

        <Flex justify="end">
          <Button
            htmlType="submit"
            loading={isGenerating}
            className="btn-primary min-w-[80px] h-11 font-semibold"
          >
            Generate
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManagePayroll;
